import React, { Fragment, useEffect, useMemo, useRef, useState }  from 'react';
import Swal from 'sweetalert2';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

import { useAuth } from '../../Context/Context';

import SideNav from '../../Components/SideNav';
import Loader from '../../Components/Loader';
import PanelEdicion from '../../Components/PanelEdicion';
import PreviewSitio from '../../Components/PreviewSitio';
import SeccionPanel from '../../Components/SeccionPanel';

import usePanelEdicion, { confirmarDescartar } from '../../Hooks/usePanelEdicion';
import usePreviewSitio from '../../Hooks/usePreviewSitio';
import useStorage from '../../Hooks/useStorage';

import profesoresHelper from '../../Helpers/Profesores';
import { recorrerPosiciones } from '../../Helpers/posiciones';

const TIPOS = [
    { valor: 2, nombre: 'Principales', ubicacion: 'Sección "Principales" de la página de docentes.' },
    { valor: 1, nombre: 'Coordinador de licenciatura', ubicacion: 'Sección "Coordinadores de Licenciatura".' },
    { valor: 0, nombre: 'Docente', ubicacion: 'Sección "Docentes", en orden alfabético.' }
];

const NUEVO = { tipo: 0, nombre: '', categoria: '', materias: '', orden: 0, archivo: '' };

const estiloAyuda = { color: "#757575", fontSize: "13px", margin: "-10px 0 10px" };

// Las fotos viejas están en profesores/ (nombradas por docente); las nuevas en imagenes/docentes/
const esFotoDocente = url => typeof url === 'string' && (url.includes('/o/imagenes%2Fdocentes%2F') || url.includes('/o/profesores%2F'));

const tieneTexto = html => (html ?? '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() !== '';

const Profesores = () => {
    const { editar, setEditar, index, setIndex, data, setData, recargar } = useAuth();

    const [docente, setDocente] = useState({ ...editar });
    const [nuevo, setNuevo] = useState(NUEVO);
    const [fotoEditar, setFotoEditar] = useState(null);
    const [fotoNueva, setFotoNueva] = useState(null);
    const [seccionActiva, setSeccionActiva] = useState(null);
    // Remonta los formularios al limpiar: CKEditor y el input de archivo no son controlados
    const [version, setVersion] = useState(0);

    const [subiendo, setSubiendo] = useState(false);
    const [consulta, setConsulta] = useState('');
    const [respuestaStorage] = useStorage(consulta);
    const recargarTrasBorrar = useRef(false);

    useEffect(() => {
        obtenerProfesores();
    }, []);

    useEffect(() => {
        setDocente({ ...editar });
    }, [editar]);

    useEffect(() => {
        if (respuestaStorage.action === 'agregar') guardarNuevo(respuestaStorage.url);
        if (respuestaStorage.action === 'agregarActualizado') guardarEdicion(respuestaStorage.url);
        if (respuestaStorage.action === 'eliminarSinConfirmar') {
            setConsulta('');
            // recargar() remonta la vista; si se llama antes de esta respuesta, el borrado no termina
            if (recargarTrasBorrar.current) recargar();
            else setSubiendo(false);
        }
    }, [respuestaStorage]);

    const obtenerProfesores = async () => {
        let res = await profesoresHelper.obtenerProfesores();
        if (res.success) {
            setData(res.data);
        } else {
            Swal.fire('Error!', 'No se pudieron obtener los docentes, codigo: ' + res.code, 'error');
        }
    }

    const hayCambiosEditar = Boolean(editar._id) && JSON.stringify(docente) !== JSON.stringify(editar);
    const hayCambiosNuevo = Boolean(nuevo.nombre || nuevo.categoria || tieneTexto(nuevo.materias) || fotoNueva);

    // actualizarBorrador modifica `data` en vivo para la vista previa; `editar` conserva el original
    const limpiarBorradores = () => {
        if (index !== false) setData(data => data.map((dato, i) => (i === index ? editar : dato)));
        setEditar({});
        setIndex(false);
        setNuevo(NUEVO);
        setFotoEditar(null);
        setFotoNueva(null);
        setSeccionActiva(null);
        setVersion(version => version + 1);
    }

    const panel = usePanelEdicion({ hayCambios: hayCambiosEditar || hayCambiosNuevo, onCerrar: limpiarBorradores });

    const abrirPanel = async (nombre, alAbrir) => {
        if ((hayCambiosEditar || hayCambiosNuevo) && !(await confirmarDescartar())) return;
        limpiarBorradores();
        alAbrir?.();
        panel.abrir(nombre);
    }

    const seleccionarDocente = ({ id }) => {
        const indice = data.findIndex(profesor => profesor._id === id);
        if (indice < 0) return;
        abrirPanel('editar', () => {
            setEditar(indice === index ? editar : data[indice]);
            setIndex(indice);
        });
    }

    // Principales y Coordinadores se recorren cada uno en su sección; los docentes van por nombre
    const datosPreview = useMemo(() => {
        const hayNuevo = Boolean(nuevo.nombre || nuevo.archivo);
        const lista = hayNuevo ? [...data, { ...nuevo, _id: 'nuevo' }] : data;
        const movido = hayNuevo ? { _id: 'nuevo', ...nuevo } : editar._id ? { _id: editar._id, ...docente } : null;
        if (!movido) return lista;
        const secciones = [1, 2].flatMap(tipo => recorrerPosiciones(
            lista.filter(profesor => profesor.tipo === tipo),
            'orden',
            movido.tipo === tipo ? movido._id : undefined,
            movido.tipo === tipo ? movido.orden : 1
        ));
        return [...lista.filter(profesor => profesor.tipo !== 1 && profesor.tipo !== 2), ...secciones];
    }, [data, nuevo, editar._id, docente]);

    // Cuántas posiciones tiene la sección del borrador (contándolo a él)
    const maximaPosicion = borrador => data.filter(profesor => profesor.tipo === borrador.tipo && profesor._id !== borrador._id).length + 1;

    const { iframeRef, src } = usePreviewSitio({
        page: 'profesores',
        ruta: '/docentes',
        data: datosPreview,
        onSelect: seleccionarDocente
    });

    const actualizarBorrador = cambios => {
        setDocente(docente => ({ ...docente, ...cambios }));
        setData(data => data.map((dato, i) => (i === index ? { ...dato, ...cambios } : dato)));
    }

    const cambiarNuevo = cambios => setNuevo(nuevo => ({ ...nuevo, ...cambios }));

    // El iframe es de otro origen y no puede leer un blob: del admin, por eso se manda como data URL
    const leerFoto = (e, cambiar, setFoto) => {
        const archivo = e.target.files[0];
        if (!archivo) return;
        const lector = new FileReader();
        lector.addEventListener('load', () => {
            setFoto(archivo);
            cambiar({ archivo: lector.result });
        });
        lector.readAsDataURL(archivo);
    }

    const faltantes = (borrador, foto) => {
        const lista = [];
        if (!borrador.nombre?.trim()) lista.push('Nombre');
        if (borrador.tipo === 0 && !tieneTexto(borrador.materias)) lista.push('Materias');
        if (borrador.tipo !== 0 && !borrador.categoria?.trim()) lista.push('Puesto');
        if (!foto) lista.push('Foto');
        return lista;
    }

    const posicionInvalida = borrador => {
        if (borrador.tipo === 0) return null;
        const maximo = maximaPosicion(borrador);
        if (Number.isInteger(borrador.orden) && borrador.orden >= 1 && borrador.orden <= maximo) return null;
        return 'La posición debe ser un número entre 1 y ' + maximo;
    }

    const borrarFoto = (url, recargarDespues) => {
        recargarTrasBorrar.current = recargarDespues;
        setConsulta({ consulta: 'eliminarSinConfirmar', urlFile: url });
    }

    const subirFoto = (accion, archivo) => {
        setSubiendo(true);
        setConsulta({ consulta: accion, name: 'docente_' + Date.now(), folder: 'imagenes/docentes/', file: archivo, url: '/profesores' });
    }

    const agregar = e => {
        e.preventDefault();
        const lista = faltantes(nuevo, fotoNueva);
        if (lista.length) return Swal.fire('Error!', 'Faltan datos por llenar: ' + lista.join(', '), 'error');
        const errorPosicion = posicionInvalida(nuevo);
        if (errorPosicion) return Swal.fire('Error!', errorPosicion, 'error');
        subirFoto('agregar', fotoNueva);
    }

    const guardarNuevo = async url => {
        // useStorage solo vuelve a ejecutarse si cambia el texto de la consulta
        setConsulta('');
        if (!url) {
            setSubiendo(false);
            return Swal.fire('Error!', 'No se pudo subir la foto', 'error');
        }
        const res = await profesoresHelper.agregarProfesor({ ...nuevo, archivo: url });
        if (res?.success) return recargar();
        // Sin registro que la use, la foto recién subida quedaría huérfana
        borrarFoto(url, false);
    }

    const actualizar = e => {
        e.preventDefault();
        const lista = faltantes(docente, docente.archivo);
        if (lista.length) return Swal.fire('Error!', 'Faltan datos por llenar: ' + lista.join(', '), 'error');
        const errorPosicion = posicionInvalida(docente);
        if (errorPosicion) return Swal.fire('Error!', errorPosicion, 'error');
        if (fotoEditar) return subirFoto('agregarActualizado', fotoEditar);
        setSubiendo(true);
        guardarEdicion();
    }

    // `url` solo llega cuando se subió una foto nueva
    const guardarEdicion = async url => {
        setConsulta('');
        if (fotoEditar && !url) {
            setSubiendo(false);
            return Swal.fire('Error!', 'No se pudo subir la foto', 'error');
        }
        const anterior = editar.archivo;
        const res = await profesoresHelper.editarProfesor({ ...docente, archivo: url ?? anterior }, editar._id);
        if (!res?.success) {
            if (url) return borrarFoto(url, false);
            return setSubiendo(false);
        }
        // Se borra hasta que el registro ya apunta a la nueva, para que el sitio nunca quede con una imagen rota
        if (url && esFotoDocente(anterior)) return borrarFoto(anterior, true);
        recargar();
    }

    const eliminar = async () => {
        const { isConfirmed } = await Swal.fire({
            icon: 'question',
            titleText: '¿Eliminar a ' + editar.nombre + '?',
            text: 'También se elimina su foto.',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });
        if (!isConfirmed) return;
        setSubiendo(true);
        const res = await profesoresHelper.eliminarProfesor(editar._id);
        if (!res?.success) return setSubiendo(false);
        if (esFotoDocente(editar.archivo)) return borrarFoto(editar.archivo, true);
        recargar();
    }

    const seccion = clave => ({ id: clave, activa: seccionActiva === clave, onFocus: () => setSeccionActiva(clave) });

    const campos = (borrador, cambiar, setFoto, foto, prefijo) => {
        const tipo = TIPOS.find(({ valor }) => valor === borrador.tipo) ?? TIPOS[2];
        const id = campo => prefijo + campo;
        const leer = e => cambiar({ [e.target.name]: e.target.value });

        return (
            <Fragment>
                <SeccionPanel numero={1} titulo='Datos' ubicacion={tipo.ubicacion} {...seccion('datos')}>
                    <div style={{marginBottom:"20px"}}>
                        <label htmlFor={id('tipo')}>Tipo</label>
                        <select
                            className='browser-default'
                            id={id('tipo')}
                            value={borrador.tipo}
                            onChange={e => {
                                // Al entrar a Principales o Coordinadores se coloca al final de esa sección
                                const tipo = Number(e.target.value);
                                cambiar({ tipo, orden: tipo === 0 ? 0 : maximaPosicion({ ...borrador, tipo }) });
                            }}
                        >
                            {TIPOS.map(({ valor, nombre }) => (
                                <option key={valor} value={valor}>{nombre}</option>
                            ))}
                        </select>
                    </div>
                    <div className="input-field">
                        <input onChange={leer} value={borrador.nombre ?? ''} id={id('nombre')} name='nombre' type="text" className="validate" />
                        <label className='active' htmlFor={id('nombre')}>Nombre</label>
                    </div>
                    {borrador.tipo !== 0 && (
                        <Fragment>
                            <div className="input-field">
                                <input onChange={leer} value={borrador.categoria ?? ''} id={id('categoria')} name='categoria' type="text" className="validate" />
                                <label className='active' htmlFor={id('categoria')}>Puesto</label>
                            </div>
                            <div className="input-field">
                                <input
                                    onChange={e => cambiar({ orden: e.target.value === '' ? '' : Number(e.target.value) })}
                                    value={borrador.orden ?? 1}
                                    id={id('orden')}
                                    type="number"
                                    min="1"
                                    max={maximaPosicion(borrador)}
                                    className="validate"
                                />
                                <label className='active' htmlFor={id('orden')}>Posición</label>
                            </div>
                            <p style={estiloAyuda}>1 = primero de la sección (de 1 a {maximaPosicion(borrador)}). Al cambiarla, los demás se recorren.</p>
                        </Fragment>
                    )}
                </SeccionPanel>

                <SeccionPanel numero={2} titulo='Foto' ubicacion='Imagen de la tarjeta; el texto aparece encima al pasar el mouse.' {...seccion('foto')}>
                    {borrador.archivo ? (
                        <img alt={borrador.nombre || 'Foto del docente'} src={borrador.archivo} style={{width:"100%", maxWidth:"220px", borderRadius:"10px"}} />
                    ) : (
                        <p style={{color:"#757575"}}>Aún no hay foto.</p>
                    )}
                    <div className="file-field input-field">
                        <div className="btn" style={{backgroundColor:"#00496C"}}>
                            <span>Archivo</span>
                            <input onChange={e => leerFoto(e, cambiar, setFoto)} type="file" accept="image/*" />
                        </div>
                        <div className="file-path-wrapper">
                            <input className="file-path" type="text" placeholder="Selecciona una imagen" value={foto?.name ?? ''} readOnly />
                        </div>
                    </div>
                    {prefijo === 'editar_' && (
                        <p style={{color:"#757575", fontSize:"13px", margin:"0"}}>Al reemplazarla, la foto anterior se elimina.</p>
                    )}
                </SeccionPanel>

                {borrador.tipo === 0 && (
                    <SeccionPanel numero={3} titulo='Materias' ubicacion='Aparecen sobre la foto al pasar el mouse.' {...seccion('materias')}>
                        <CKEditor
                            editor={ClassicEditor}
                            data={borrador.materias ?? ''}
                            onChange={(event, editor) => cambiar({ materias: editor.getData() })}
                        />
                    </SeccionPanel>
                )}
            </Fragment>
        );
    }

    return (
        <Fragment>
            <SideNav />
            {subiendo && (
                <div style={{width:"100%", height:"100vh", position:"fixed", top:"0", background:"#00000059", zIndex:"9999"}}>
                    <Loader />
                </div>
            )}
            <div className='row'>
                <PanelEdicion
                    abierto={panel.abierto === 'agregar'}
                    titulo='Agregar docente'
                    ancho={panel.ancho}
                    onArrastre={panel.iniciarArrastre}
                    onCerrar={() => panel.cerrar()}
                    hayCambios={hayCambiosNuevo}
                    pie={
                        <button type='submit' form='form_agregar_docente' className='boton_admin' disabled={!hayCambiosNuevo}>
                            Agregar docente
                        </button>
                    }
                >
                    {panel.abierto === 'agregar' && (
                        <form id='form_agregar_docente' key={version} onSubmit={agregar}>
                            {campos(nuevo, cambiarNuevo, setFotoNueva, fotoNueva, 'nuevo_')}
                        </form>
                    )}
                </PanelEdicion>
                <PanelEdicion
                    abierto={panel.abierto === 'editar'}
                    titulo='Editar docente'
                    ancho={panel.ancho}
                    onArrastre={panel.iniciarArrastre}
                    onCerrar={() => panel.cerrar()}
                    hayCambios={hayCambiosEditar}
                    pie={
                        <Fragment>
                            <button type='button' className='boton_admin boton_admin_peligro' onClick={eliminar}>
                                Eliminar
                            </button>
                            <button type='submit' form='form_editar_docente' className='boton_admin' disabled={!hayCambiosEditar}>
                                Guardar cambios
                            </button>
                        </Fragment>
                    }
                >
                    {panel.abierto === 'editar' && editar._id && (
                        <form id='form_editar_docente' key={version} onSubmit={actualizar}>
                            {campos(docente, actualizarBorrador, setFotoEditar, fotoEditar, 'editar_')}
                        </form>
                    )}
                </PanelEdicion>
                <PreviewSitio
                    iframeRef={iframeRef}
                    src={src}
                    titulo='Vista previa de docentes'
                    margen={panel.abierto ? panel.ancho : 0}
                    encabezado='Docentes'
                    ayuda='Haz clic en un docente para editarlo'
                    acciones={
                        <button type='button' className='boton_admin' onClick={() => abrirPanel('agregar')}>
                            + Agregar docente
                        </button>
                    }
                />
            </div>
        </Fragment>
    );
}

export default Profesores;
